/**
 * Case studies and client reviews.
 *
 * IMPORTANT: everything in this file is published on the website and, where
 * marked, emitted as structured data. Only add genuine, de-identified matters
 * and genuine client reviews. Nothing here is invented or illustrative.
 *
 * Legal/SEO notes:
 * - Case studies must not identify a client and must not promise outcomes.
 *   Queensland Law Society advertising rules and the Legal Profession Act
 *   apply. Keep results factual and add no guarantees.
 * - Review structured data is only emitted when a review is genuine and has
 *   been given to the firm. We deliberately do NOT emit aggregateRating on
 *   self-hosted reviews — Google treats self-serving aggregate ratings for a
 *   LocalBusiness as ineligible for rich results.
 */

import { FIRM, FIRM_ADDRESS } from "./firm";

export type CaseStudyCategory = "conveyancing" | "wills-estates";

export type CaseStudy = {
  /** Short slug, used for anchor links. */
  id: string;
  category: CaseStudyCategory;
  /** Plain-language headline, e.g. "Unit purchase with an undisclosed levy". */
  title: string;
  /** Suburb or region — helps local relevance. Optional. */
  location?: string;
  /** What the client came to us with. */
  situation: string;
  /** What we actually did. */
  action: string;
  /** What happened. Factual only — no guarantees, no dollar figures unless confirmed. */
  outcome: string;
};

export type ClientReview = {
  id: string;
  /** First name and suburb only, exactly as the client published it. */
  author: string;
  location?: string;
  /** Verbatim review text. Do not paraphrase or tidy up. */
  body: string;
  /** Star rating out of 5, if the source platform gave one. */
  rating?: 1 | 2 | 3 | 4 | 5;
  /** e.g. "Google review". Where the review was originally left. */
  source?: string;
  /** ISO date the review was published, if known. */
  datePublished?: string;
  category?: CaseStudyCategory;
};

/**
 * Real, de-identified matters. Add entries as they are approved for publication.
 */
export const CASE_STUDIES: ReadonlyArray<CaseStudy> = [
  {
    id: "parental-loan-and-mortgage",
    category: "wills-estates",
    title: "Protecting a family loan used to help a son buy a home",
    location: "Sunshine Coast",
    situation:
      "Parents came to us wanting to help their adult son into a property. Their plan was simply to transfer the money and trust that it would come back to them one day. We talked through what that would mean if the relationship between their son and his partner broke down, if he ran into financial trouble, or if one of the parents died before the money was repaid. A gift, once made, is gone: it forms part of the son's asset pool, and the parents would be unsecured and unrecorded if anything went wrong.",
    action:
      "We advised that a properly documented loan, secured by a registered mortgage over the property, gave protection to both sides. It gave the parents a recorded, enforceable and secured debt rather than an informal understanding, and it gave the son a clear record that the funds were not a gift or a contribution by anyone else. We prepared a written loan agreement setting out the amount advanced, repayment terms, interest treatment and events of default, prepared the mortgage and attended to its registration against the title at settlement, and made sure the arrangement was consistent with the parents' wills so the debt was dealt with sensibly if either of them died before repayment. Both the parents and the son were advised to consider independent advice so that everyone understood what they were signing.",
    outcome:
      "Years later the money was challenged, with a claim advanced that the funds formed part of the pool available on the son's side. Because there was a contemporaneous written loan agreement and a registered mortgage on the title, the advance was treated as a secured debt owed to the parents rather than as a gift or as an asset available to be divided. The documents did the work, and the parents recovered their money. The same paperwork would have protected the parents' estate had one of them died before repayment, because the debt was recorded and secured rather than left to memory.",
  },
  {
    id: "sale-to-fund-retirement-village-capacity-challenge",
    category: "wills-estates",
    title: "Elderly parents' right to sell their own home upheld against a challenge by their children",
    location: "Sunshine Coast",
    situation:
      "An elderly couple decided to sell the family home to fund their move into a retirement village. They had made the decision themselves, understood what they were doing and why, and had chosen the village they wanted to move to. After the property was placed on the market, two of their adult children objected and took steps to stop the sale going ahead, asserting that their parents no longer had the capacity to make a decision of that kind. The practical effect was that the parents — who had done nothing wrong and had full legal capacity — were at risk of being unable to deal with their own property or fund the accommodation they had chosen.",
    action:
      "We acted for the parents. The starting point in Queensland is that an adult is presumed to have capacity, and a registered owner is entitled to deal with their own property; a family member's disagreement with a decision is not evidence that the decision-maker lacks capacity. We took careful instructions directly from each parent, separately, and made a contemporaneous record of the reasons for the sale and their understanding of it. We obtained an independent medical assessment of capacity directed to the specific decision being made, rather than a general opinion. We assembled the evidence of the parents' decision-making — their choice of village, the financial reasons for the move and the steps they had already taken — and put the objectors on notice that any interference with the sale was unsupported. When the objection was not withdrawn, we brought the issue before the court and presented the capacity evidence and the parents' own instructions.",
    outcome:
      "The court intervened and ruled in favour of the parents. Their capacity to make the decision was accepted and their right to deal with their own property was upheld, and the sale was able to proceed so they could fund the move into the retirement village they had chosen. The matter turned on evidence: a decision-specific capacity assessment and a clear, contemporaneous record of instructions taken directly from the owners. Disagreement by adult children about how a parent uses their own asset is not, of itself, a basis to restrain a sale.",
  },
  {
    id: "granny-flat-arrangement-and-estate-planning",
    category: "wills-estates",
    title: "A granny flat arrangement documented properly, with the wills brought into line",
    location: "Sunshine Coast",
    situation:
      "Parents whose health was declining were considering moving in with one of their children. The plan was for the parents to contribute a substantial sum towards building an extension so they would have their own space, with the expectation that they could stay there for the rest of their lives and that the child would provide day-to-day care. Nothing was written down. We talked through what could go wrong: the child's relationship breaking down, the child needing or wanting to sell, the parents' care needs outgrowing what the household could manage, one parent dying, or the arrangement simply not working out. In each of those situations the parents would have paid for an improvement to someone else's property with no right to remain and no clear right to get their money back. We also raised the Centrelink gifting and granny flat interest rules, and the resentment such arrangements commonly cause among siblings who see the family money go to one child.",
    action:
      "We advised the parents on the risks in writing and prepared a granny flat agreement recording the contribution, what it bought, the parents' right to occupy for life, who pays for what (rates, insurance, utilities, maintenance), what care was and was not being promised, and — most importantly — what happens if the arrangement ends: the trigger events, how the property would be dealt with, and how the parents' contribution would be repaid or adjusted. We addressed the position if the child died or separated, and dealt with occupancy and repayment on the death of the first parent. We recommended each party obtain independent legal advice so that the agreement could not later be attacked as a product of pressure. We then updated both parents' wills so that the estate plan matched the arrangement rather than contradicting it: the contribution and any repayment obligation were accounted for, and the gifts to the other children were adjusted so that each child was treated fairly overall.",
    outcome:
      "The family moved ahead with the arrangement on documented terms. The parents have a recorded right to occupy and a defined entitlement to be repaid if the arrangement ends, the child hosting them knows exactly what is expected and what is not, and the other children can see how the contribution has been accounted for in the wills. Documenting the arrangement and aligning the wills at the same time removed the two most common sources of later dispute: an undocumented contribution and an estate plan that no longer reflects what the parents actually did.",
  },
  {
    id: "agent-purchase-below-market-value",
    category: "conveyancing",
    title: "Stopping a sale where the selling agent had contracted to buy the property himself",
    location: "Queensland",
    situation:
      "Owners came to us after signing a contract to sell their property. The buyer under the contract was the selling agent they had engaged to market it, and the price was substantially below what the property was worth. The sellers had not been given an independent valuation, had not been told what comparable properties were achieving, and did not appreciate that the person advising them on price was the same person standing to profit from the purchase. By the time they sought advice the contract was on foot and settlement was approaching.",
    action:
      "We reviewed the contract and the circumstances in which it had been signed, and gathered evidence of market value and of what the sellers had and had not been told. An agent who acquires their principal's property occupies a position of conflict: Queensland's agency legislation imposes strict disclosure and consent requirements on an agent who has a beneficial interest in a transaction, and the general law imposes fiduciary duties that do not permit an agent to prefer their own interest over their client's. We put the agent and his solicitors on notice of the sellers' position, identified the grounds on which the sellers said the contract could not stand, and pressed for the contract to be brought to an end rather than allowing settlement to occur and leaving the sellers to chase compensation afterwards. We acted quickly, because stopping a transfer is far more effective than trying to unwind one.",
    outcome:
      "The contract was terminated before settlement and the property was returned to the market. The sellers went on to resell it at a materially higher price than the agent had contracted to pay. The lesson is a practical one: if the person selling your property, or someone connected to them, wants to buy it, that is the moment to get independent legal advice and an independent valuation — before you sign, and certainly before settlement.",
  },
];

/**
 * Genuine client reviews. Add verbatim only.
 */
export const CLIENT_REVIEWS: ReadonlyArray<ClientReview> = [];

export const CATEGORY_LABELS: Record<CaseStudyCategory, string> = {
  conveyancing: "Conveyancing & property",
  "wills-estates": "Wills, estates & elder law",
};

/**
 * Review structured data, attached to the firm's Attorney entity.
 * Returns null when there are no genuine reviews, so we never publish empty
 * or fabricated review markup.
 */
export function reviewSchema(reviews: ReadonlyArray<ClientReview>) {
  const usable = reviews.filter((r) => r.body.trim().length > 0);
  if (usable.length === 0) return null;

  return {
    "@context": "https://schema.org",
    "@type": "Attorney",
    "@id": "#coastside-law",
    name: FIRM.name,
    telephone: FIRM.phone,
    address: {
      "@type": "PostalAddress",
      streetAddress: FIRM.street,
      addressLocality: FIRM.suburb,
      addressRegion: FIRM.state,
      postalCode: FIRM.postcode,
      addressCountry: FIRM.country,
    },
    description: `${FIRM.name}, ${FIRM_ADDRESS}`,
    review: usable.map((r) => ({
      "@type": "Review",
      author: { "@type": "Person", name: r.author },
      reviewBody: r.body,
      ...(r.datePublished ? { datePublished: r.datePublished } : {}),
      ...(r.rating
        ? {
            reviewRating: {
              "@type": "Rating",
              ratingValue: r.rating,
              bestRating: 5,
              worstRating: 1,
            },
          }
        : {}),
    })),
  };
}

/**
 * Case studies expressed as an ItemList of Articles so AI search engines can
 * read them as discrete examples of our work. Null when there are none.
 */
export function caseStudyListSchema(items: ReadonlyArray<CaseStudy>) {
  if (items.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: `${FIRM.name} case studies`,
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      item: {
        "@type": "Article",
        headline: item.title,
        articleSection: CATEGORY_LABELS[item.category],
        description: item.situation,
        author: { "@type": "Organization", name: FIRM.name },
      },
    })),
  };
}
